import React, { useEffect, useRef, useState } from "react";
import { Input } from "antd";
import { getHighlightedText } from "../../../../utils/highlight";
import { Match } from "../../../../hooks/useFindReplace";
import { TranscriptionBlockType } from "../../../../utils/type";
import TextAreaActionPanel from "./TextAreaActionPanel";
import { useAppDispatch } from "../../../../redux/useAppDispatch";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { setCurrentFocusBlockIndex, setHighlightText, updateBlock } from "../../../../redux/JobModule/QCDetailPage/QCDetailPageAction";
import parse from "html-react-parser";
import { highlightedTextType } from "../../../../redux/JobModule/QCDetailPage/QCDetailPageSlice";
const { TextArea } = Input;

interface Props {
  transcript: TranscriptionBlockType;
  index: number;
  texts: TranscriptionBlockType[];
  updateTranscriptText: (newTexts: TranscriptionBlockType[]) => void;
  allMatches: Match[];
  currentMatchIndex: number;
  disabled: boolean;
}

const TextAreaWithHighlight: React.FC<Props> = ({ transcript, index, texts, updateTranscriptText, allMatches, currentMatchIndex, disabled }) => {
  const textareaRef = useRef<any>(null);
  const highlightLayerRef = useRef<HTMLDivElement>(null);
  const [showHighlight, setShowHighlight] = useState(false);
  const idTranscription = useSelector((state: RootState) => state.qCDetailPageState.data.transcriptions[0].idTranscription);
  const [currentText, setCurrentText] = React.useState(transcript.transcript);
  const [previousText, setPreviousText] = React.useState(transcript.transcript);
  const [selectedHighlightText, setSelectedText] = useState("");
  const highlightText = useSelector((state: RootState) => state.qCDetailPageState.highlightedText.highlightText);
  const [mouseOnSplitButton, setMouseOnSplitButton] = useState(false);
  const currentSplitButton = document.getElementById(`split-button-${index}`);
  useEffect(() => {
    if (currentSplitButton) {
      currentSplitButton.addEventListener("mouseover", () => {
        setMouseOnSplitButton(true);
      });
      currentSplitButton.addEventListener("mouseout", () => {
        setMouseOnSplitButton(false);
      });
    }
    return () => {
      if (currentSplitButton) {
        currentSplitButton.removeEventListener("mouseover", () => {
          setMouseOnSplitButton(true);
        });
        currentSplitButton.removeEventListener("mouseout", () => {
          setMouseOnSplitButton(false);
        });
      }
    };
  }, [currentSplitButton]);
  const dispatch = useAppDispatch();
  const getTextareaElement = () => textareaRef.current?.resizableTextArea?.textArea as HTMLTextAreaElement | undefined;

  const syncHighlightScroll = () => {
    const textarea = getTextareaElement();
    if (!textarea || !highlightLayerRef.current) return;

    highlightLayerRef.current.scrollTop = textarea.scrollTop;
    highlightLayerRef.current.scrollLeft = textarea.scrollLeft;
  };

  const updateBlockTranscript = async (value: string) => {
    const newBlock = {
      TranscriptionId: idTranscription,
      TranscriptionBlocks: [
        {
          TranscriptionBlockId: transcript.idTranscriptionBlock,
          StartTimeSec: transcript.startTimeSec,
          EndTimeSec: transcript.endTimeSec,
          Speaker: transcript.speaker,
          Text: value,
        },
      ],
    };
    await dispatch(updateBlock({ id: idTranscription, data: newBlock, blockIndex: index }));
    //This set previous is being used to check if the text is changed or not, so this will reduce the amount of API calls that we need to do.
    setPreviousText(value);
  };
  useEffect(() => {
    setCurrentText(transcript.transcript);
    setPreviousText(transcript.transcript);
  }, [transcript.transcript]);
  const getSelectedText = () => {
    // Check if there is a selection and it is not empty
    // Check if the selection is not empty and is within the text area

    if (textareaRef.current) {
      const startIndex = textareaRef.current?.resizableTextArea.textArea.selectionStart;
      const endIndex = textareaRef.current?.resizableTextArea.textArea.selectionEnd;

      //I want to remove only the spaces from the start and end of the selected text
      const transcriptText = currentText; // Trim the transcript text to remove trailing spaces
      const selectedText = currentText.substring(startIndex, endIndex);
      setSelectedText(selectedText.trim());

      // Ensure valid indices and determine the position of the selected text
      let positionType: "FULL" | "START" | "END" | "CENTER";
      let remainingText: string[];
      if (startIndex === 0 && endIndex === transcriptText.length) {
        positionType = "FULL";
        remainingText = [];
      } else if (startIndex === 0 && selectedText.length > 0) {
        // Ensure valid start index and non-empty selection
        positionType = "START";
        //Remaining text for this should be the text at the end of the selected text until the end text
        remainingText = [transcriptText.slice(endIndex)];
      } else if (endIndex === transcriptText.length) {
        positionType = "END";
        //Remaining text for this should be the text at the start of the selected text until the end text
        remainingText = [transcriptText.slice(0, startIndex)];
      } else {
        positionType = "CENTER";
        //Remaining text for this should be the text at the start and end of the selected text then the selected text until the end text
        remainingText = [transcriptText.slice(0, startIndex), transcriptText.slice(endIndex)];
      }

      const highlightText: highlightedTextType = {
        highlightText: selectedText,
        type: positionType,
        remainingText: remainingText,
      };

      dispatch(setHighlightText(highlightText));
    }
  };

  useEffect(() => {
    setCurrentText(transcript.transcript);
  }, [transcript.transcript]);

  useEffect(() => {
    syncHighlightScroll();
  }, [currentText, allMatches, currentMatchIndex, showHighlight]);

  return (
    <div>
      <TextAreaActionPanel
        transcript={transcript}
        currentText={currentText}
        disabled={disabled}
        index={index}
        lastElement={index === texts.length - 1}
      />
      <div style={{ position: "relative", marginBottom: "10px" }}>
        {/* Highlighted Layer */}
        {!showHighlight && (
          <div
            ref={highlightLayerRef}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              padding: "8px",
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
              fontFamily: "inherit",
              fontSize: "16px",
              lineHeight: "1.5",
              color: "black",
              zIndex: 1,
              overflow: "hidden",
              pointerEvents: "none",
              borderRadius: "0px",
              boxSizing: "border-box",
            }}>
            {parse(getHighlightedText(currentText, index, allMatches, currentMatchIndex))}
          </div>
        )}

        {/* Actual TextArea */}
        <TextArea
          value={currentText}
          ref={textareaRef}
          onFocus={() => {
            dispatch(setCurrentFocusBlockIndex(index));
            setShowHighlight(true);
          }}
          id={`text-area-${index}`}
          onChange={(e) => {
            setPreviousText(currentText);
            setCurrentText(e.target.value);
          }}
          onBlur={(e) => {
            setShowHighlight(false);
            getSelectedText();
            const startIndex = textareaRef.current?.resizableTextArea.textArea.selectionStart;
            const endIndex = textareaRef.current?.resizableTextArea.textArea.selectionEnd;
            const selectedText = e.target.value.substring(startIndex, endIndex);
            if (currentText !== previousText && !mouseOnSplitButton) {
              updateBlockTranscript(e.target.value);
              return;
            } else if (currentText !== previousText && mouseOnSplitButton && selectedText === "") {
              updateBlockTranscript(e.target.value);
              return;
            } else if (currentText === previousText && selectedText !== "") {
              // If the text is not changed but there is a highlight text, we want to update the block transcript with the current text
              // This is to ensure that the highlight text is saved in the transcript
            }

            // if (currentText !== previousText && (!mouseOnSplitButton || highlightText === "")) {
            //   updateBlockTranscript(e.target.value);
            // }
          }}
          // onMouseUp={getSelectedText} // Capture selection when mouse is released
          // onMouseOut={getSelectedText} // Capture selection when mouse is released
          disabled={disabled}
          autoSize={{ minRows: 3 }}
          onScroll={syncHighlightScroll}
          style={{
            position: "relative",
            background: "transparent",
            fontSize: "16px",
            lineHeight: "1.5",
            fontFamily: "inherit",
            zIndex: 2,
            color: showHighlight ? "black" : "transparent",
            WebkitTextFillColor: showHighlight ? "black" : "transparent",
            caretColor: "black",
            borderRadius: "0px",
            borderTop: "none",
          }}
        />
      </div>
    </div>
  );
};

export default TextAreaWithHighlight;
